/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MapInteractionsService } from '../../services/mapInteractions.service';
import { QueryService } from '../../services/query.service';
import { Select, Store } from '@ngxs/store';
import { UpdateSelectionMode } from './state/polygon-select.actions';
import { PolygonSelectModel } from './state/polygon-select.model';
import { Observable, Subject } from 'rxjs';
import { PolygonSelectState } from './state/polygon-select.state';
import { concatMap, takeUntil } from 'rxjs/operators';
import { DialogRef } from '@angular/cdk/dialog';
import { MatCard, MatCardContent, MatCardActions } from '@angular/material/card';
import { LayoutDirective, LayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'dash-gis-select-mode-panel',
    templateUrl: './polygon-select-mode-panel.component.html',
    styleUrls: ['./polygon-select-mode-panel.component.scss'],
    imports: [
        MatCard,
        MatCardContent,
        LayoutDirective,
        MatRadioGroup,
        FormsModule,
        MatRadioButton,
        MatCardActions,
        LayoutAlignDirective,
        MatButton,
        AsyncPipe,
    ],
})
export class PolygonSelectModePanelComponent implements OnInit, OnDestroy {
    public selectionMode$: Observable<string> = inject(Store).select(PolygonSelectState.getMode);

    private dispose$ = new Subject<void>();

    constructor(
        private dialogRef: DialogRef<PolygonSelectModePanelComponent>,
        private queryService: QueryService,
        private mapInteractions: MapInteractionsService,
        private store: Store
    ) {}

    setSelectionMode(value) {
        const payload: PolygonSelectModel = { mode: value };
        this.store.dispatch(new UpdateSelectionMode(payload));
    }

    ngOnInit() {
        this.mapInteractions.enablePolygonSelect(this.selectFunction.bind(this));
    }

    ngOnDestroy() {
        this.mapInteractions.clearPolygonSelect();
        this.mapInteractions.enablePanInteraction();
        this.dispose$.next();
    }

    close() {
        this.dialogRef.close();
    }

    selectFunction(evt) {
        this.selectionMode$
            .pipe(concatMap((selectionMode) => this.queryService.selectWithinFeature$([evt.feature], selectionMode)))
            .subscribe(() => {
                this.dialogRef.close();
            });
    }

    get selectionModes() {
        return ['Intersects', 'Within'];
    }
}
